import React, { ReactElement, useContext } from 'react'
// Commons imports.
import { InlinePromptProps } from '../InlinePrompt/InlinePrompt'
import { ModalExitPromptContextProvider } from './ModalExitPromptContext'
import Dialog from './Dialog'
import { LayeredModal } from './LayeredModal'
import { CommonFormContext, FormContextType } from '../../Contexts/CommonFormContext'
import { $window } from '../../../../NonReact/common/AkixiWindow'
// CSS imports. Commented out because of current build process.
// import './modal.scss'
/**
 * Component properties type.
 */
type Props = {
    classNames?: string
    children: ReactElement | ReactElement[]
    isOpen: boolean
    onClose?: () => void
    onOpen?: () => void
    closeOnBackdropClick?: boolean
    isClosableWithXButton?: boolean
    inlineExitPromptSettings?: InlinePromptProps
    onExited?: () => void
    layered?: boolean
    isViewOnly?: boolean
}
/**
 * Modal component. Actually the contexts wrapper for the dialog implementation.
 *
 * @param classNames a set of class names to add to the modal
 * @param children Modal window content.
 * @param isOpen Whether the modal window is open.
 * @param onClose Callback on close.
 * @param onOpen Callback on open.
 * @param closeOnBackdropClick Whether the click on modal window backdrop should close the modal window.
 * @param isClosableWithXButton Whether it is possible to close with the 'X' button displayed in top right corner.
 * @param inlineExitPromptSettings Properties for prompt on modal close.
 * @param onExited Callback when the modal is closed. Designed to be used when the modal is interacting with DOM
 * @param isViewOnly View only flag.
 * directly and it is necessary to do the clean-up after the modal is closed.
 */
const Modal: React.FC<Props> = ({
    classNames,
    children,
    isOpen,
    onClose,
    onOpen,
    closeOnBackdropClick,
    isClosableWithXButton,
    inlineExitPromptSettings,
    onExited,
    layered,
    isViewOnly
}: Props): ReactElement => {
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    return (
        <ModalExitPromptContextProvider>
            {layered || $window.m_bIsFullscreenActive ? (
                <LayeredModal
                    classNames={classNames}
                    isOpen={isOpen}
                    onClose={onClose}
                    isClosableWithXButton={isClosableWithXButton}
                >
                    {children}
                </LayeredModal>
            ) : (
                <Dialog
                    classNames={classNames}
                    isOpen={isOpen}
                    onClose={onClose}
                    onOpen={onOpen}
                    closeOnBackdropClick={!!closeOnBackdropClick}
                    isClosableWithXButton={isClosableWithXButton}
                    inlineExitPromptSettings={
                        !!commonForm && !commonForm.isTouched ? undefined : inlineExitPromptSettings
                    }
                    onExited={onExited}
                    isViewOnly={isViewOnly}
                >
                    {children}
                </Dialog>
            )}
        </ModalExitPromptContextProvider>
    )
}
export default Modal
